<script setup lang="ts">
import { updates } from '@/data/updates'
import { ref, computed } from 'vue'
import UpdateContent from '@/components/UpdateContent.vue'

const searchQuery = ref('')
const activeTab = ref('all')

const filteredUpdates = computed(() => {
  let result = updates

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(
      (update) => update.update.toLowerCase().includes(query) || update.version.toLowerCase().includes(query)
    )
  }

  if (activeTab.value !== 'all') {
    const versionPrefix = activeTab.value
    result = result.filter((update) => update.version.startsWith(versionPrefix))
  }

  return result
})

const versionGroups = computed(() => {
  const groups = updates.reduce((acc, update) => {
    const majorVersion = `${update.version.split('.')[0]}.${update.version.split('.')[1]}`
    if (!acc.includes(majorVersion)) {
      acc.push(majorVersion)
    }
    return acc
  }, [] as string[])

  return groups
})

function formatDate(date: Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>

<template>
  <div class="updates-container">
    <div class="row justify-between items-center q-mb-md">
      <div class="text-h4 text-weight-bold">Product Updates</div>
      <q-input v-model="searchQuery" dense outlined placeholder="Search updates..." class="search-input">
        <template v-slot:prepend>
          <q-icon name="mdi-text-search-variant" />
        </template>
        <template v-slot:append v-if="searchQuery">
          <q-icon name="mdi-close" @click="searchQuery = ''" class="cursor-pointer" />
        </template>
      </q-input>
    </div>

    <q-tabs
      v-model="activeTab"
      dense
      class="text-grey q-mb-md"
      active-color="primary"
      indicator-color="primary"
      align="left"
      narrow-indicator
    >
      <q-tab name="all" label="All Updates" />
      <q-tab v-for="group in versionGroups" :key="group" :name="group" :label="`v${group}`" />
    </q-tabs>

    <div class="timeline-container">
      <q-timeline color="primary">
        <q-timeline-entry
          v-for="(update, index) in filteredUpdates"
          :key="index"
          :title="`Version ${update.version}`"
          :subtitle="formatDate(update.date)"
          icon="mdi-update"
        >
          <q-card flat bordered class="update-card">
            <q-card-section>
              <div class="text-h5 q-mb-sm text-secondary">{{ update.title }}</div>
              <UpdateContent :content="update.update" />
            </q-card-section>
          </q-card>
        </q-timeline-entry>
      </q-timeline>

      <div v-if="filteredUpdates.length === 0" class="text-center q-pa-xl">
        <q-icon name="mdi-emoticon-sad-outline" size="4rem" color="grey-5" />
        <div class="text-h6 text-grey-7 q-mt-md">No updates found matching your search</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.updates-container {
  max-width: 1000px;
  margin: 0 auto;
}

.search-input {
  width: 300px;
}

.timeline-container {
  padding-bottom: 50px;
}

.update-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.update-card:hover {
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

@media (max-width: 600px) {
  .search-input {
    width: 100%;
    margin-top: 16px;
  }
}
</style>
